/**
 * InlineRiskAssessment — opens CAMBRA + EFP 2018 risk assessment INSIDE the
 * Dental Health Intelligence module (no external navigation).
 *
 * Posts to POST /api/dental/screening and renders the categorised result
 * inline. After a successful run, the parent DHI page is notified via
 * `onSaved` so the snapshot is refreshed and the new authoritative scores
 * propagate to Caries / Periodontal / Microbiome cards.
 *
 * Compact — uses 12 binary toggles + age slider. Mirror of DentalScreeningPage
 * questionnaire, but as an embedded panel instead of a standalone route.
 */
import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ClipboardCheck, ChevronDown, ChevronUp, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUESTIONS_BG = [
  { key: "active_caries_lesions",    label: "Активен кариес или нелекуван зъб" },
  { key: "white_spot_lesions",       label: "Бели петна по зъбите" },
  { key: "frequent_snacking",        label: "Захар/нишесте >3× дневно" },
  { key: "sugary_drinks_daily",      label: "Сладки напитки всеки ден" },
  { key: "dry_mouth",                label: "Често сухота в устата" },
  { key: "exposed_root_surfaces",    label: "Отдръпнати венци / видим корен" },
  { key: "fluoride_toothpaste_2x",   label: "Миене 2×/ден с флуоридна паста", protective: true },
  { key: "regular_dental_visits",    label: "Редовни прегледи (поне 1×/год.)", protective: true },
  { key: "gums_bleed_brushing",      label: "Венците кървят при миене", section: "perio" },
  { key: "teeth_mobility",           label: "Разклатени зъби", section: "perio" },
  { key: "bad_breath_persistent",    label: "Постоянен лош дъх", section: "perio" },
  { key: "acute_pain",               label: "Силна остра болка СЕГА", section: "redflag" },
];

const QUESTIONS_EN = [
  { key: "active_caries_lesions",    label: "Active cavity or untreated tooth" },
  { key: "white_spot_lesions",       label: "White spots on teeth" },
  { key: "frequent_snacking",        label: "Sugars/starches >3×/day" },
  { key: "sugary_drinks_daily",      label: "Sweet drinks daily" },
  { key: "dry_mouth",                label: "Often dry mouth" },
  { key: "exposed_root_surfaces",    label: "Receded gums / exposed roots" },
  { key: "fluoride_toothpaste_2x",   label: "Brush 2×/day with fluoride", protective: true },
  { key: "regular_dental_visits",    label: "Regular check-ups (≥1×/year)", protective: true },
  { key: "gums_bleed_brushing",      label: "Gums bleed when brushing", section: "perio" },
  { key: "teeth_mobility",           label: "Loose teeth", section: "perio" },
  { key: "bad_breath_persistent",    label: "Persistent bad breath", section: "perio" },
  { key: "acute_pain",               label: "Severe acute tooth pain RIGHT NOW", section: "redflag" },
];

const CATEGORY_LABEL_BG = {
  low: "Нисък", moderate: "Умерен", high: "Висок", extreme: "Много висок",
  low_or_no_signs: "Нисък — без признаци",
  low_to_moderate: "Нисък до умерен",
  moderate_symptom_only: "Умерен (по симптоми)",
  low_to_moderate_symptom_only: "Нисък до умерен (по симптоми)",
};
const CATEGORY_LABEL_EN = {
  low: "Low", moderate: "Moderate", high: "High", extreme: "Extreme",
  low_or_no_signs: "Low — no signs",
  low_to_moderate: "Low to moderate",
  moderate_symptom_only: "Moderate (symptom-based)",
  low_to_moderate_symptom_only: "Low to moderate (symptom-based)",
};

const CAT_PILL = {
  low: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
  low_or_no_signs: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
  low_to_moderate: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
  moderate: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
  moderate_symptom_only: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
  low_to_moderate_symptom_only: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
  high: "bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300",
  extreme: "bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300",
};

export default function InlineRiskAssessment({ isDark, lang = "bg", onSaved, cardCls }) {
  const bg = lang === "bg";
  const t = bg
    ? {
        title: "Дентална оценка на риска (CAMBRA + EFP 2018)",
        subtitle: "Изчислява се вътре в модула — без излизане към друга страница.",
        run: "Преоцени риска",
        runFirst: "Стартирай оценка",
        running: "Изчислява…",
        age: "Възраст",
        flags: "Предупредителни признаци",
        caries: "Кариес",
        perio: "Пародонтално",
        recompute: "Нов анализ",
        saved: "Резултатите са записани и DHI scores са обновени.",
        error: "Грешка. Опитайте отново.",
      }
    : {
        title: "Dental risk assessment (CAMBRA + EFP 2018)",
        subtitle: "Computed inside the module — no external navigation.",
        run: "Re-assess risk",
        runFirst: "Start assessment",
        running: "Computing…",
        age: "Age",
        flags: "Red flags",
        caries: "Caries",
        perio: "Periodontal",
        recompute: "New analysis",
        saved: "Results saved and DHI scores updated.",
        error: "Error. Please try again.",
      };

  const QUESTIONS = bg ? QUESTIONS_BG : QUESTIONS_EN;
  const CAT_LABEL = bg ? CATEGORY_LABEL_BG : CATEGORY_LABEL_EN;

  const [expanded, setExpanded] = useState(true);  // Start expanded so user sees the form immediately
  const [answers, setAnswers] = useState({});
  // iter1110 — do NOT prefill age to 45. An unentered age quietly biased
  // CAMBRA toward adult risk tables and often produced a false "low risk"
  // result for teens. Force the user to enter their real age.
  const [age, setAge] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  const toggle = (key) => setAnswers((p) => ({ ...p, [key]: !p[key] }));

    const submit = async () => {
    setSubmitting(true);
    setErr(null);
    try {
      // iter1110 — require a real age before submitting; the backend risk
      // engine falls back to adult CAMBRA when age is missing, which is
      // the wrong table for teens.
      const ageNum = parseInt(age, 10);
      if (!Number.isFinite(ageNum) || ageNum < 1 || ageNum > 120) {
        setErr(bg ? "Моля въведете възрастта (1-120)" : "Please enter age (1-120)");
        setSubmitting(false);
        return;
      }
      // The dental engine expects answers nested under `answers`.
      const { age: _a, age_group: _g, ...flatAnswers } = answers;
      const payload = {
        age: ageNum,
        age_group: "adult",
        answers: { ...flatAnswers },
      };
      const { data } = await axios.post(`${API}/dental/screening`, payload);
      setResult(data);
      // Notify parent — DHI snapshot reload re-anchors caries/perio scores
      if (onSaved) onSaved(data);
      // iter1110 — broadcast so any listening DHI card refreshes.
      try {
        window.dispatchEvent(new CustomEvent("nutramilo:dental:updated"));
      } catch { /* no-op */ }
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Render compact summary if collapsed and we have a result
  const hasResult = !!result;
  const caries = result?.risk_assessment?.caries;
  const perio = result?.risk_assessment?.periodontal;
  const redFlags = result?.red_flags || [];

  return (
    <div className={`${cardCls} p-5`} data-testid="dhi-inline-risk-assessment">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center justify-between gap-3 text-left"
        data-testid="dhi-risk-toggle"
      >
        <div className="flex items-center gap-2">
          <ClipboardCheck className="w-5 h-5 text-amber-500" />
          <div>
            <h3 className="text-lg font-bold">{t.title}</h3>
            <p className="text-xs opacity-60">{t.subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasResult && (
            <div className="flex flex-wrap gap-1.5">
              {caries && (
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${CAT_PILL[caries.category]}`} data-testid="risk-caries-pill">
                  {t.caries}: {CAT_LABEL[caries.category]}
                </span>
              )}
              {perio && (
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${CAT_PILL[perio.category]}`} data-testid="risk-perio-pill">
                  {t.perio}: {CAT_LABEL[perio.category]}
                </span>
              )}
            </div>
          )}
          {expanded ? <ChevronUp className="w-4 h-4 opacity-60" /> : <ChevronDown className="w-4 h-4 opacity-60" />}
        </div>
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="pt-4 space-y-4">
              <div className="flex items-center gap-3">
                <label className="text-xs font-semibold">{t.age}</label>
                <input
                  type="number"
                  value={age}
                  min={1}
                  max={120}
                  placeholder={bg ? "години" : "years"}
                  onChange={(e) => setAge(e.target.value.replace(/[^\d]/g, ""))}
                  className={`w-24 px-2 py-1 rounded border text-sm ${
                    isDark ? "bg-white/5 border-white/10" : "bg-white border-gray-300"
                  }`}
                  data-testid="risk-age-input"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-2">
                {QUESTIONS.map((q) => (
                  <label
                    key={q.key}
                    className={`flex items-start gap-2 p-2 rounded-lg border cursor-pointer text-xs ${
                      answers[q.key]
                        ? (q.protective
                            ? (isDark ? "bg-emerald-500/15 border-emerald-500/30" : "bg-emerald-50 border-emerald-300")
                            : (isDark ? "bg-rose-500/15 border-rose-500/30" : "bg-rose-50 border-rose-300"))
                        : (isDark ? "bg-white/[0.02] border-white/5 hover:bg-white/5" : "bg-gray-50 border-gray-200 hover:bg-gray-100")
                    }`}
                    data-testid={`risk-q-${q.key}`}
                  >
                    <input
                      type="checkbox"
                      checked={!!answers[q.key]}
                      onChange={() => toggle(q.key)}
                      className="mt-0.5 shrink-0"
                    />
                    <span className="leading-snug">{q.label}</span>
                  </label>
                ))}
              </div>

              <button
                type="button"
                onClick={submit}
                disabled={submitting}
                data-testid="risk-submit-btn"
                className={`w-full px-4 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                  submitting
                    ? "opacity-60 cursor-not-allowed"
                    : "hover:scale-[1.01]"
                } ${
                  isDark
                    ? "bg-amber-500/20 border border-amber-500/40 text-amber-200"
                    : "bg-amber-500 text-white"
                }`}
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t.running}
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <ClipboardCheck className="w-4 h-4" />
                    {hasResult ? t.recompute : t.runFirst}
                  </span>
                )}
              </button>

              {err && (
                <div className="text-xs p-2 rounded bg-rose-500/10 text-rose-500">{err}</div>
              )}

              {hasResult && (
                <div className="space-y-2" data-testid="risk-result-block">
                  {/* Big result tiles — make it OBVIOUS that the assessment was saved */}
                  <div className="grid grid-cols-2 gap-2">
                    {caries && (
                      <div
                        className={`p-3 rounded-xl border-2 text-center ${CAT_PILL[caries.category]}`}
                        data-testid="risk-result-caries"
                      >
                        <div className="text-[10px] uppercase opacity-70 mb-0.5">{t.caries}</div>
                        <div className="text-lg font-extrabold leading-tight">
                          {CAT_LABEL[caries.category] || caries.category}
                        </div>
                        <div className="text-[10px] mt-0.5 opacity-80 font-mono">
                          {bg ? "Скор" : "Score"}: {caries.score_0_10?.toFixed(1)} / 10
                        </div>
                      </div>
                    )}
                    {perio && (
                      <div
                        className={`p-3 rounded-xl border-2 text-center ${CAT_PILL[perio.category]}`}
                        data-testid="risk-result-perio"
                      >
                        <div className="text-[10px] uppercase opacity-70 mb-0.5">{t.perio}</div>
                        <div className="text-lg font-extrabold leading-tight">
                          {CAT_LABEL[perio.category] || perio.category}
                        </div>
                        <div className="text-[10px] mt-0.5 opacity-80 font-mono">
                          {bg ? "Скор" : "Score"}: {perio.score_0_10?.toFixed(1)} / 10
                        </div>
                      </div>
                    )}
                  </div>

                  <div className={`p-3 rounded-lg flex items-start gap-2 text-xs ${
                    isDark ? "bg-emerald-500/10 text-emerald-200" : "bg-emerald-50 text-emerald-700"
                  }`}>
                    <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>{t.saved}</span>
                  </div>

                  {redFlags.length > 0 && (
                    <div className={`p-3 rounded-lg flex items-start gap-2 text-xs ${
                      isDark ? "bg-rose-500/10 text-rose-200" : "bg-rose-50 text-rose-700"
                    }`} data-testid="risk-redflag-warning">
                      <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                      <span>
                        {t.flags}: {redFlags.join(", ")} — {bg ? "потърсете зъболекар незабавно." : "see a dentist immediately."}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
